import React, { useRef, useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import styles from './Brevehistoire.module.css';

const Brevehistoire = () => {
  const location = useLocation();
  const scrollContainerRef = useRef(null);
  const mainNavScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollMainLeft, setCanScrollMainLeft] = useState(false);
  const [canScrollMainRight, setCanScrollMainRight] = useState(false);
  
  // Définir les sous-sections pour chaque chapitre
  const subSections = {
    royaumes: [
      { label: 'De la coexistence des royaumes claniques…', hash: '#section-1' },
      { label: 'De l\'unification', hash: '#unification' },
      { label: 'Des royaumes encore autonomes…', hash: '#autonomes' }
    ],
    europeens: [
      { label: 'Les Allemands', hash: '#allemands' },
      { label: 'Vers l\'occupation du territoire rwandais', hash: '#occupation' },
      { label: 'Le territoire rwandais administré', hash: '#administration' },
      { label: 'Extension du pouvoir « sindi-nyiginya »', hash: '#extension' },
      { label: 'Les Belges', hash: '#belges' },
      { label: 'Des « autorités souveraines » aux « auxiliaires »', hash: '#autorites' },
      { label: 'Structures administratives', hash: '#structures' },
      { label: 'Les Chefs autochtones', hash: '#chefs' },
      { label: 'L\'avènement de l\'ONU', hash: '#onu' },
      { label: 'Le contrôle du système de tutelle', hash: '#controle' }
    ],
    annexes: [
      { label: 'Gouverneurs (Deutschostafrika)', hash: '#gouverneurs' },
      { label: 'Responsables militaires', hash: '#militaires' },
      { label: 'Résidents du Rwanda', hash: '#residents' },
      { label: 'Tableau chronologique des règnes', hash: '#chronologie' },
      { label: 'Cartographie', hash: '#cartographie' }
    ]
  };

  // Déterminer la section active
  const currentSection = location.pathname.split('/').pop() || 'introduction';
  const currentSubSections = subSections[currentSection] || [];

  // Vérifier la position du scroll
  const checkScroll = () => {
    // Pour le sous-sommaire
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
    
    // Pour le sommaire principal
    if (mainNavScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = mainNavScrollRef.current;
      setCanScrollMainLeft(scrollLeft > 0);
      setCanScrollMainRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.addEventListener('scroll', checkScroll);
    }
    if (mainNavScrollRef.current) {
      mainNavScrollRef.current.addEventListener('scroll', checkScroll);
    }
    return () => {
      window.removeEventListener('resize', checkScroll);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', checkScroll);
      }
      if (mainNavScrollRef.current) {
        mainNavScrollRef.current.removeEventListener('scroll', checkScroll);
      }
    };
  }, [currentSubSections]);

  // Auto-scroll vers le chapitre actif dans le nav principal
  useEffect(() => {
    if (mainNavScrollRef.current) {
      const activeLink = mainNavScrollRef.current.querySelector(`a[class*="${styles.tabActive}"]`);
      if (activeLink) {
        setTimeout(() => {
          activeLink.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }, 100);
      }
    }
  }, [currentSection]);

  // Auto-scroll vers le lien actif quand on clique sur un sous-sommaire
  const handleSubSectionClick = (hash) => {
    if (scrollContainerRef.current) {
      // Trouver l'élément cliqué
      const link = scrollContainerRef.current.querySelector(`a[href="${hash}"]`);
      if (link) {
        // Scroll le container pour centrer le lien
        setTimeout(() => {
          link.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }, 50);
      }
    }
  };

  // Scroll horizontal avec ref flexible
  const handleScroll = (direction, ref) => {
    if (ref && ref.current) {
      const scrollAmount = 200;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  return (
    <div className={styles.page}>
      <div className={styles.layout}>
        
        {/* HERO : Titre principal */}
        <header className={styles.hero}>
          <div className={styles.heroKicker}>Histoire</div>
          <h1 className={styles.heroTitle}>Brève Histoire du Rwanda</h1>
          <p className={styles.heroSub}>
            Des origines jusqu'à la fin de la colonisation
          </p>
        </header>

        {/* SOMMAIRE (Sticky Tabs) */}
        <div className={styles.navWrapper}>
          <nav className={styles.subnav}>
            <ul ref={mainNavScrollRef}>
              <li>
                <NavLink 
                  to="introduction" 
                  className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
                >
                  1. Introduction
                </NavLink>
              </li>
              <li>
                <NavLink 
                  to="royaumes" 
                  className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
                >
                  2. Royaumes
                </NavLink>
              </li>
              <li>
                <NavLink 
                  to="europeens" 
                  className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
                >
                  3. Européens
                </NavLink>
              </li>
              <li>
                <NavLink 
                  to="conclusion" 
                  className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
                >
                  4. Conclusion
                </NavLink>
              </li>
              <li>
                <NavLink 
                  to="annexes" 
                  className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
                >
                  5. Annexes
                </NavLink>
              </li>
            </ul>
          </nav>
        </div>

        {/* SOUS-SOMMAIRE (Sous-sections de la page active) */}
        {currentSubSections.length > 0 && (
          <div className={styles.subSectionNavWrapper}>
            <nav className={styles.subSectionNav}>
              <ul ref={scrollContainerRef}>
                {currentSubSections.map((section, index) => (
                  <li key={index}>
                    <a 
                      href={section.hash} 
                      className={styles.subTab}
                      onClick={() => handleSubSectionClick(section.hash)}
                    >
                      {section.label}
                    </a>
                    {index < currentSubSections.length - 1 && <span className={styles.separator}>▸</span>}
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}

        {/* CONTENU PRINCIPAL */}
        <main className={styles.content}>
          <Outlet />
        </main>

      </div>
    </div>
  );
};

export default Brevehistoire;